import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import ActionNotice from "@/components/base/ActionNotice";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsArticles } from "@/mocks/news";
import { copyCurrentLink } from "@/utils/share";
import { formatDate, formatViews } from "@/utils/format";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = newsArticles.find((item) => item.id === id);
  const [notice, setNotice] = useState("");

  usePageMeta({
    title: article ? `${article.title} - ${article.category} - 成人网` : "资讯详情 - 成人网",
    description: article
      ? `${article.excerpt.slice(0, 80)} 成人网${article.category}栏目，作者 ${article.author}。`
      : "成人网资讯详情页，提供行业观察与观看健康相关的深度阅读。",
    keywords: article
      ? `${article.title},${article.category},${article.tags.join(",")},成人资讯`
      : "成人资讯,资讯详情,成人网",
  });

  const related = useMemo(() => {
    if (!article) {
      return newsArticles.slice(0, 3);
    }
    const sameCategory = newsArticles.filter(
      (item) => item.id !== article.id && item.category === article.category,
    );
    const others = newsArticles.filter(
      (item) => item.id !== article.id && item.category !== article.category,
    );
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="container-page py-10">
        <EmptyState
          icon="ri-newspaper-line"
          title="没有找到这篇资讯"
          description="链接可能已经失效，或者这篇文章已被移动。可以回到资讯分类继续浏览。"
          actionLabel="返回资讯分类"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/news-category");
          }}
        />
      </div>
    );
  }

  const handleShare = async () => {
    const ok = await copyCurrentLink();
    setNotice(ok ? "链接已复制，可以分享给朋友了" : "复制失败，请手动复制浏览器地址");
  };

  return (
    <div className="container-page py-7">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "资讯分类", to: "/news-category" },
          { label: article.title },
        ]}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_318px]">
        <article className="min-w-0">
          <span className="rounded-sm bg-accent-500/15 px-2 py-0.5 text-[11px] text-accent-500">
            {article.category}
          </span>
          <h1 className="mt-3 font-heading text-[26px] leading-snug text-foreground-950 md:text-[34px]">
            {article.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-200 pb-4 text-[11.5px] text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {formatDate(article.publishDate)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)} 阅读
            </span>
            <button
              type="button"
              onClick={handleShare}
              className="ml-auto flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-[11px] text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>

          <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <img
              src={article.cover}
              alt={`${article.title} - 资讯头图`}
              title={`${article.title} 成人资讯`}
              className="h-[220px] w-full object-cover md:h-[340px]"
            />
          </div>

          <p className="mt-6 border-l-2 border-accent-500/60 pl-4 text-[13.5px] leading-[1.95] text-foreground-700">
            {article.excerpt}
          </p>

          <div className="mt-6 space-y-5">
            {article.body.map((paragraph, index) => (
              <div key={`${article.id}-p-${index}`}>
                <p className="text-[14px] leading-[2] text-foreground-600">{paragraph}</p>
                {index === 1 ? (
                  <div className="mt-5 overflow-hidden rounded-lg border border-background-200">
                    <img
                      src={article.cover}
                      alt={`${article.title} 正文配图`}
                      title={`${article.title} 资讯配图`}
                      className="h-[180px] w-full object-cover md:h-[240px]"
                    />
                    <p className="bg-background-100 px-3 py-2 text-[10.5px] text-foreground-500">
                      示意图 · 与文章内容相关的演示配图
                    </p>
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap gap-1.5">
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between rounded-lg border border-background-200 bg-background-100 px-5 py-4">
            <div>
              <p className="text-xs text-foreground-950">喜欢这篇文章？</p>
              <p className="mt-1 text-[11px] text-foreground-500">
                复制链接分享，或到社区继续这个话题。
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleShare}
                className="rounded-md border border-background-300 px-3.5 py-2 text-[11px] text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500 whitespace-nowrap cursor-pointer"
              >
                分享
              </button>
              <Link
                to="/community"
                className="rounded-md bg-primary-500 px-3.5 py-2 text-[11px] text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                去社区
              </Link>
            </div>
          </div>
        </article>

        <aside className="min-w-0 space-y-6">
          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">相关文章</h2>
            <div className="mt-2">
              {related.map((item) => (
                <NewsCard key={item.id} article={item} />
              ))}
            </div>
          </section>

          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">分类导航</h2>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {["资讯动态", "行业报道", "健康知识", "文化观察"].map((item) => (
                <Link
                  key={item}
                  to="/news-category"
                  className="rounded-full bg-background-200 px-2.5 py-1 text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
                >
                  {item}
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>

      {notice ? <ActionNotice message={notice} onClose={() => setNotice("")} /> : null}
    </div>
  );
}